import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Input,
  Option,
  Select,
  Stack,
  Typography
} from '@mui/joy';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FenestrationFilterOptions,
  FenestrationFilters,
  FenestrationMetric,
  FenestrationQueryItem,
  getFenestrationFilterOptions,
  searchFenestrationItems
} from '../services/fenestrationQuery.service';

const PAGE_SIZE = 24;

const EMPTY_OPTIONS: FenestrationFilterOptions = {
  internalFinishes: [],
  externalFinishes: [],
  materialTypes: [],
  itemTypes: [],
  styles: [],
  glassTypes: [],
  profileTypes: [],
  beadingTypes: [],
  handleColors: [],
  handleStyles: [],
  hingeTypes: [],
  jointStyles: [],
  cillOptions: [],
  hingePlacements: [],
  openDirections: [],
  sources: []
};

const VALID_METRICS = new Set<FenestrationMetric>([
  'internal_finish',
  'external_finish',
  'material_type',
  'type',
  'glass_type',
  'equal_dims',
  'within_50_dims',
  'mean_window',
  'mean_door',
  'openers',
  'all'
]);

const METRIC_LABELS: Record<FenestrationMetric, string> = {
  internal_finish: 'Internal finish',
  external_finish: 'External finish',
  material_type: 'Material type',
  type: 'Item type',
  glass_type: 'Glass type',
  equal_dims: 'Exact dimension matches',
  within_50_dims: 'Dimension matches within 50mm',
  mean_window: 'Windows with dimensions',
  mean_door: 'Doors with dimensions',
  openers: 'Items with openers',
  all: 'All items'
};

const getMetric = (value: string | null): FenestrationMetric =>
  value && VALID_METRICS.has(value as FenestrationMetric)
    ? value as FenestrationMetric
    : 'all';

const filtersFromSearchParams = (params: URLSearchParams): FenestrationFilters => ({
  search: params.get('q') || '',
  metric: getMetric(params.get('metric')),
  internalFinish: params.get('internal_finish') || '',
  externalFinish: params.get('external_finish') || '',
  materialType: params.get('material') || '',
  itemType: params.get('type') || '',
  style: params.get('style') || '',
  glassType: params.get('glass') || '',
  profileType: params.get('profile') || '',
  beadingType: params.get('beading') || '',
  handleColor: params.get('handle_color') || '',
  handleStyle: params.get('handle_style') || '',
  hingeType: params.get('hinge') || '',
  jointStyle: params.get('joint') || '',
  cillOption: params.get('cill') || '',
  hingePlacement: params.get('hinge_placement') || '',
  openDirection: params.get('open_direction') || '',
  source: params.get('source') || '',
  georgianBars: params.get('georgian_bars') === 'yes'
    ? 'yes'
    : params.get('georgian_bars') === 'no' ? 'no' : '',
  astragalBars: params.get('astragal_bars') === 'yes'
    ? 'yes'
    : params.get('astragal_bars') === 'no' ? 'no' : '',
  trickleVents: params.get('trickle_vents') === 'yes'
    ? 'yes'
    : params.get('trickle_vents') === 'no'
      ? 'no'
      : '',
  minWidthMm: params.get('min_width') || '',
  maxWidthMm: params.get('max_width') || '',
  minHeightMm: params.get('min_height') || '',
  maxHeightMm: params.get('max_height') || '',
  minOpeners: params.get('min_openers') || '',
  documentKind: params.get('kind') || '',
  createdFrom: params.get('from'),
  createdTo: params.get('to')
});

const setOptionalParam = (params: URLSearchParams, key: string, value: string) => {
  if (value) params.set(key, value);
  else params.delete(key);
};

const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

const formatNumber = (value: number) => new Intl.NumberFormat('en-GB').format(value);

const toPublicImageUrl = (path: string): string => {
  if (!path || path.startsWith('http://') || path.startsWith('https://') || path.startsWith('/')) return path;
  return `/${path}`;
};

const SelectFilter: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => (
  <Box>
    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>{label}</Typography>
    <Select
      value={value}
      placeholder={`All ${label.toLowerCase()}`}
      onChange={(_, nextValue) => onChange(nextValue || '')}
      sx={{ minWidth: 0 }}
    >
      <Option value="">All</Option>
      {options.map((option) => <Option key={option} value={option}>{option}</Option>)}
    </Select>
  </Box>
);

const FenestrationResultCard: React.FC<{ item: FenestrationQueryItem }> = ({ item }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box>
            <Typography level="title-sm">
              <Link to={`/docs/${item.documentId}`}>{item.documentRef}</Link>
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              {item.documentKind} · {item.documentTitle}
            </Typography>
          </Box>
          <Chip size="sm" variant="soft">x{formatNumber(item.qty)}</Chip>
        </Stack>

        <Typography level="title-md">{item.lineName || item.itemType}</Typography>
        {item.imageUrl ? (
          <Box
            component="img"
            src={toPublicImageUrl(item.imageUrl)}
            alt={item.itemType}
            loading="lazy"
            sx={{
              width: '100%',
              height: 150,
              objectFit: 'contain',
              borderRadius: '8px',
              bgcolor: 'background.level1'
            }}
          />
        ) : (
          <Box sx={{
            height: 90,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            bgcolor: 'background.level1',
            borderRadius: '8px'
          }}>
            <Typography level="body-xs">No preview image</Typography>
          </Box>
        )}

        <Divider />
        <Grid container spacing={0.75}>
          <Grid xs={6}><Typography level="body-xs"><strong>Type:</strong> {item.itemType}</Typography></Grid>
          <Grid xs={6}><Typography level="body-xs"><strong>Style:</strong> {item.styleName}</Typography></Grid>
          <Grid xs={6}><Typography level="body-xs"><strong>Material:</strong> {item.materialType}</Typography></Grid>
          <Grid xs={6}><Typography level="body-xs"><strong>Glass:</strong> {item.glassType}</Typography></Grid>
          <Grid xs={6}><Typography level="body-xs"><strong>Internal:</strong> {item.internalFinish}</Typography></Grid>
          <Grid xs={6}><Typography level="body-xs"><strong>External:</strong> {item.externalFinish}</Typography></Grid>
          <Grid xs={6}>
            <Typography level="body-xs">
              <strong>Size:</strong> {item.widthMm > 0 && item.heightMm > 0
                ? `${formatNumber(Math.round(item.widthMm))} × ${formatNumber(Math.round(item.heightMm))} mm`
                : '—'}
            </Typography>
          </Grid>
          <Grid xs={6}><Typography level="body-xs"><strong>Openers:</strong> {formatNumber(item.openerCount)}</Typography></Grid>
          <Grid xs={6}><Typography level="body-xs"><strong>Profile:</strong> {item.profileType}</Typography></Grid>
          <Grid xs={6}><Typography level="body-xs"><strong>Beading:</strong> {item.beadingType}</Typography></Grid>
        </Grid>
      </Stack>
    </CardContent>
  </Card>
);

const FenestrationPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FenestrationFilters>(() => filtersFromSearchParams(searchParams));
  const [options, setOptions] = useState<FenestrationFilterOptions>(EMPTY_OPTIONS);
  const [items, setItems] = useState<FenestrationQueryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => Math.max(1, Number(searchParams.get('page') || 1)));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const queryFilters = useDebouncedValue(filters, 400);
  const period = searchParams.get('period') || '';

  const updateFilter = <K extends keyof FenestrationFilters>(key: K, value: FenestrationFilters[K]) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  useEffect(() => {
    let active = true;
    getFenestrationFilterOptions()
      .then((nextOptions) => {
        if (active) setOptions(nextOptions);
      })
      .catch(() => {
        // Results remain usable if optional facet loading fails.
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    searchFenestrationItems(queryFilters, page, PAGE_SIZE)
      .then((result) => {
        if (!active) return;
        setItems(result.items);
        setTotal(result.total);
      })
      .catch((err: any) => {
        if (!active) return;
        setItems([]);
        setTotal(0);
        setError(err?.message || 'Failed to search fenestration items');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [queryFilters, page]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    setOptionalParam(next, 'q', filters.search);
    setOptionalParam(next, 'metric', filters.metric === 'all' ? '' : filters.metric);
    setOptionalParam(next, 'internal_finish', filters.internalFinish);
    setOptionalParam(next, 'external_finish', filters.externalFinish);
    setOptionalParam(next, 'material', filters.materialType);
    setOptionalParam(next, 'type', filters.itemType);
    setOptionalParam(next, 'style', filters.style);
    setOptionalParam(next, 'glass', filters.glassType);
    setOptionalParam(next, 'profile', filters.profileType);
    setOptionalParam(next, 'beading', filters.beadingType);
    setOptionalParam(next, 'handle_color', filters.handleColor);
    setOptionalParam(next, 'handle_style', filters.handleStyle);
    setOptionalParam(next, 'hinge', filters.hingeType);
    setOptionalParam(next, 'joint', filters.jointStyle);
    setOptionalParam(next, 'cill', filters.cillOption);
    setOptionalParam(next, 'hinge_placement', filters.hingePlacement);
    setOptionalParam(next, 'open_direction', filters.openDirection);
    setOptionalParam(next, 'source', filters.source);
    setOptionalParam(next, 'georgian_bars', filters.georgianBars);
    setOptionalParam(next, 'astragal_bars', filters.astragalBars);
    setOptionalParam(next, 'trickle_vents', filters.trickleVents);
    setOptionalParam(next, 'min_width', filters.minWidthMm);
    setOptionalParam(next, 'max_width', filters.maxWidthMm);
    setOptionalParam(next, 'min_height', filters.minHeightMm);
    setOptionalParam(next, 'max_height', filters.maxHeightMm);
    setOptionalParam(next, 'min_openers', filters.minOpeners);
    setOptionalParam(next, 'kind', filters.documentKind);
    setOptionalParam(next, 'page', page > 1 ? String(page) : '');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [filters, page, searchParams, setSearchParams]);

  const clearFilters = () => {
    setFilters((current) => ({
      ...filtersFromSearchParams(new URLSearchParams()),
      createdFrom: current.createdFrom,
      createdTo: current.createdTo
    }));
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstResult = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastResult = Math.min(page * PAGE_SIZE, total);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography level="h2">Fenestration Query Tool</Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Search active Visual Designer items across quotes and estimates
            {period ? ` · ${period}` : ''}
          </Typography>
        </Box>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Free-text search</Typography>
                <Input
                  value={filters.search}
                  onChange={(event) => updateFilter('search', event.target.value)}
                  placeholder='Try “French”, a reference, style, material or note'
                  startDecorator="⌕"
                />
                <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  Search runs after you pause typing.
                </Typography>
              </Box>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 1.5
              }}>
                <SelectFilter label="Internal finish" value={filters.internalFinish} options={options.internalFinishes} onChange={(value) => updateFilter('internalFinish', value)} />
                <SelectFilter label="External finish" value={filters.externalFinish} options={options.externalFinishes} onChange={(value) => updateFilter('externalFinish', value)} />
                <SelectFilter label="Material" value={filters.materialType} options={options.materialTypes} onChange={(value) => updateFilter('materialType', value)} />
                <SelectFilter label="Type" value={filters.itemType} options={options.itemTypes} onChange={(value) => updateFilter('itemType', value)} />
                <SelectFilter label="Style" value={filters.style} options={options.styles} onChange={(value) => updateFilter('style', value)} />
                <SelectFilter label="Glass" value={filters.glassType} options={options.glassTypes} onChange={(value) => updateFilter('glassType', value)} />
                <SelectFilter label="Profile" value={filters.profileType} options={options.profileTypes} onChange={(value) => updateFilter('profileType', value)} />
                <SelectFilter label="Beading" value={filters.beadingType} options={options.beadingTypes} onChange={(value) => updateFilter('beadingType', value)} />
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent="space-between">
                <Button variant="plain" onClick={() => setShowAdvanced((value) => !value)}>
                  {showAdvanced ? 'Hide advanced filters' : 'Show advanced filters'}
                </Button>
                <Button variant="outlined" color="neutral" onClick={clearFilters}>Clear filters</Button>
              </Stack>

              {showAdvanced && (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                  gap: 1.5,
                  pt: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  <SelectFilter label="Handle colour" value={filters.handleColor} options={options.handleColors} onChange={(value) => updateFilter('handleColor', value)} />
                  <SelectFilter label="Handle style" value={filters.handleStyle} options={options.handleStyles} onChange={(value) => updateFilter('handleStyle', value)} />
                  <SelectFilter label="Hinge type" value={filters.hingeType} options={options.hingeTypes} onChange={(value) => updateFilter('hingeType', value)} />
                  <SelectFilter label="Joint style" value={filters.jointStyle} options={options.jointStyles} onChange={(value) => updateFilter('jointStyle', value)} />
                  <SelectFilter label="Cill option" value={filters.cillOption} options={options.cillOptions} onChange={(value) => updateFilter('cillOption', value)} />
                  <SelectFilter label="Hinge placement" value={filters.hingePlacement} options={options.hingePlacements} onChange={(value) => updateFilter('hingePlacement', value)} />
                  <SelectFilter label="Opening direction" value={filters.openDirection} options={options.openDirections} onChange={(value) => updateFilter('openDirection', value)} />
                  <SelectFilter label="Designer source" value={filters.source} options={options.sources} onChange={(value) => updateFilter('source', value)} />
                  <Box>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Trickle vents</Typography>
                    <Select value={filters.trickleVents} onChange={(_, value) => updateFilter('trickleVents', (value || '') as FenestrationFilters['trickleVents'])}>
                      <Option value="">Any</Option>
                      <Option value="yes">Included</Option>
                      <Option value="no">Not included</Option>
                    </Select>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Georgian bars</Typography>
                    <Select value={filters.georgianBars} onChange={(_, value) => updateFilter('georgianBars', (value || '') as FenestrationFilters['georgianBars'])}>
                      <Option value="">Any</Option>
                      <Option value="yes">Included</Option>
                      <Option value="no">Not included</Option>
                    </Select>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Astragal bars</Typography>
                    <Select value={filters.astragalBars} onChange={(_, value) => updateFilter('astragalBars', (value || '') as FenestrationFilters['astragalBars'])}>
                      <Option value="">Any</Option>
                      <Option value="yes">Included</Option>
                      <Option value="no">Not included</Option>
                    </Select>
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Minimum width (mm)</Typography>
                    <Input type="number" value={filters.minWidthMm} onChange={(event) => updateFilter('minWidthMm', event.target.value)} />
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Maximum width (mm)</Typography>
                    <Input type="number" value={filters.maxWidthMm} onChange={(event) => updateFilter('maxWidthMm', event.target.value)} />
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Minimum height (mm)</Typography>
                    <Input type="number" value={filters.minHeightMm} onChange={(event) => updateFilter('minHeightMm', event.target.value)} />
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Maximum height (mm)</Typography>
                    <Input type="number" value={filters.maxHeightMm} onChange={(event) => updateFilter('maxHeightMm', event.target.value)} />
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Minimum openers</Typography>
                    <Input type="number" value={filters.minOpeners} onChange={(event) => updateFilter('minOpeners', event.target.value)} />
                  </Box>
                  <Box>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>Document type</Typography>
                    <Select value={filters.documentKind} onChange={(_, value) => updateFilter('documentKind', value || '')}>
                      <Option value="">Quotes and estimates</Option>
                      <Option value="Quotation">Quotes only</Option>
                      <Option value="Estimate">Estimates only</Option>
                    </Select>
                  </Box>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {filters.metric !== 'all' && (
          <Alert color="primary" variant="soft">
            Dashboard view: {METRIC_LABELS[filters.metric]}. Your filters are applied within this result set.
          </Alert>
        )}

        {!!error && <Alert color="danger">{error}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
          <Box>
            <Typography level="title-lg">{formatNumber(total)} matching rows</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Showing {formatNumber(firstResult)}–{formatNumber(lastResult)}
            </Typography>
          </Box>
          {loading && <CircularProgress size="sm" />}
        </Stack>

        {!loading && !error && items.length === 0 && (
          <Card variant="soft">
            <CardContent>
              <Typography level="title-md">No fenestration items matched</Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Try a broader search or clear one or more filters.
              </Typography>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={2}>
          {items.map((item) => (
            <Grid key={item.lineId} xs={12} sm={6} lg={4}>
              <FenestrationResultCard item={item} />
            </Grid>
          ))}
        </Grid>

        {totalPages > 1 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" gap={1}>
            <Typography level="body-sm">Page {formatNumber(page)} of {formatNumber(totalPages)}</Typography>
            <Stack direction="row" gap={1}>
              <Button variant="outlined" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>
                Previous
              </Button>
              <Button variant="outlined" disabled={page >= totalPages || loading} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>
                Next
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default FenestrationPage;
